import React, { useEffect, useState, useRef } from 'react';
import { NavBar } from '../components/NavBar';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { Button } from '../components/SubscriptionAppButton';
import { Send, Sparkles, MoreVertical, Download, Trash2, MessageSquare, User, HelpCircle, Bell } from 'lucide-react';
// Types for chat messages
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
}
// Mock initial messages
const initialMessages: Message[] = [{
  id: '1',
  role: 'user',
  content: 'What was our churn rate last month?',
  timestamp: '3 minutes ago'
}, {
  id: '2',
  role: 'ai',
  content: "Your churn rate last month was 2.1%, which is a 0.5% improvement compared to the previous month. The main reasons for cancellation were 'Too expensive' (34%) and 'Not using enough' (28%). Would you like me to break this down by subscription tier?",
  timestamp: '2 minutes ago'
}, {
  id: '3',
  role: 'user',
  content: 'Yes, show me by tier',
  timestamp: '1 minute ago'
}, {
  id: '4',
  role: 'ai',
  content: "Here's the churn breakdown by tier:\n\n• Premium Wine Club: 1.8% (lowest)\n• Standard Box: 2.4%\n• Starter Pack: 3.1% (highest)\n\nThe Starter Pack tier has the highest churn, likely due to lower commitment. Consider offering an upgrade incentive after the 3rd order.",
  timestamp: 'Just now'
}];
// Suggested questions
const suggestedQuestions = ['Who are my at-risk customers?', "What's my revenue forecast?", 'Show me retention trends', 'Which products are most popular?', 'Compare this month to last month'];
export function AIChatPage() {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    // Add user message
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: 'Just now'
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI response
    setTimeout(() => {
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: "I've analyzed your data based on that request. Here's what I found...",
        timestamp: 'Just now'
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1500);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6 flex flex-col h-screen overflow-hidden" data-id="element-3992">
      {/* NavBar */}
      <div className="mb-6 flex-shrink-0" data-id="element-3993">
        <NavBar tabs={[]} title="AI Chat" data-id="element-3994" />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden max-w-[1280px] mx-auto w-full" data-id="element-3995">
        {/* Stats Row */}

        {/* Chat Container */}
        <div className="bg-white rounded-lg shadow-sm border border-[#E2E8F0] flex flex-col flex-1 overflow-hidden min-h-[400px]" data-id="element-3996">
          {/* Chat Header Bar */}
          <div className="h-[56px] border-b border-[#E2E8F0] px-6 flex items-center justify-between flex-shrink-0 bg-white rounded-t-lg" data-id="element-3997">
            <div className="flex items-center gap-4" data-id="element-3998">
              <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3999">
                Conversation
              </span>
              <Button variant="secondary" size="sm" iconLeft={<MessageSquare className="w-3.5 h-3.5" data-id="element-4001" />} data-id="element-4000">
                New Chat
              </Button>
            </div>
            <div className="flex items-center gap-3" data-id="element-4002">
              <button className="text-[13px] font-medium text-[#64748B] hover:text-[#EF4444] transition-colors flex items-center gap-1.5" data-id="element-4003">
                <Trash2 className="w-3.5 h-3.5" data-id="element-4004" />
                Clear History
              </button>
              <div className="h-4 w-px bg-[#E2E8F0]" data-id="element-4005" />
              <Button variant="secondary" size="sm" iconLeft={<Download className="w-3.5 h-3.5" data-id="element-4007" />} data-id="element-4006">
                Export Chat
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-[#F8FAFC] space-y-6" data-id="element-4008">
            {messages.map(msg => <div key={msg.id} className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-4009">
                {/* Avatar */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${msg.role === 'ai' ? 'bg-[#0EA5E9] text-white' : 'bg-[#64748B] text-white'}
                `} data-id="element-4010">
                  {msg.role === 'ai' ? <Sparkles className="w-4 h-4" data-id="element-4011" /> : <span className="text-xs font-medium" data-id="element-4012">SC</span>}
                </div>

                {/* Message Bubble */}
                <div className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} max-w-[80%]`} data-id="element-4013">
                  <div className={`
                    px-4 py-3 rounded-lg shadow-sm text-[14px] leading-relaxed whitespace-pre-wrap
                    ${msg.role === 'ai' ? 'bg-white text-[#0F172A] border border-[#E2E8F0] rounded-tl-none' : 'bg-[#0EA5E9] text-white rounded-tr-none'}
                  `} data-id="element-4014">
                    {msg.content}
                  </div>
                  <span className="text-[12px] text-[#94A3B8] mt-1.5 px-1" data-id="element-4015">
                    {msg.timestamp}
                  </span>
                </div>
              </div>)}

            {isTyping && <div className="flex gap-4" data-id="element-4016">
                <div className="w-8 h-8 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center flex-shrink-0" data-id="element-4017">
                  <Sparkles className="w-4 h-4" data-id="element-4018" />
                </div>
                <div className="bg-white border border-[#E2E8F0] px-4 py-3 rounded-lg rounded-tl-none shadow-sm" data-id="element-4019">
                  <div className="flex gap-1" data-id="element-4020">
                    <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-4021" />
                    <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-4022" />
                    <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-4023" />
                  </div>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-4024" />
          </div>

          {/* Input Area */}
          <div className="bg-white border-t border-[#E2E8F0] p-6 flex-shrink-0" data-id="element-4025">
            {/* Suggested Questions */}
            <div className="mb-4" data-id="element-4026">
              <div className="flex items-center gap-2 mb-2" data-id="element-4027">
                <span className="text-[12px] font-medium text-[#64748B]" data-id="element-4028">
                  Try asking:
                </span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-4029">
                {suggestedQuestions.map((question, index) => <button key={index} onClick={() => {
                setInputValue(question);
                // Optional: auto-send
              }} className="whitespace-nowrap px-3 py-1.5 rounded-full border border-[#E2E8F0] text-[13px] text-[#475569] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors bg-white" data-id="element-4030">
                    {question}
                  </button>)}
              </div>
            </div>

            {/* Input Field */}
            <div className="flex gap-3" data-id="element-4031">
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask anything about your wine club..." className="flex-1 h-[48px] px-4 rounded-lg border border-[#E2E8F0] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all" data-id="element-4032" />
              <button onClick={handleSendMessage} disabled={!inputValue.trim() || isTyping} className="h-[48px] w-[48px] flex items-center justify-center rounded-lg bg-[#0EA5E9] text-white hover:bg-[#0284C7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-4033">
                <Send className="w-5 h-5" data-id="element-4034" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}